import React, { createContext, useState, useEffect } from 'react';
import { UserProvider } from './UserContext';


interface LoginResponse {
    success: boolean;
    loginForm: boolean;
    mfaEnabled: boolean;
    cookies?: string;
    user?: any;
    token?: string;
}

// Define the shape of the context value
interface AuthContextType {
    token: string | null;
    initialized: boolean;
    login: (username: string, password: string) => Promise<LoginResponse>;
    loginWithTFA: (username: string, password: string, tfaCode: string) => Promise<LoginResponse>;
    logout: () => void;
    isLoggedIn: () => boolean;
    refreshToken: () => void;
}

// Create the context with a default value of `null`
export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<any> = ({ children }) => {
    // const [user, setUser] = useState<{ name: string } | null>(null);
    const [jwt_token, setToken] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);

    const refreshToken = async () => {
        const tokenResponse = await fetch('/api/users/refresh', { method: 'GET' });
        if (!tokenResponse.ok) {
            return;
        }
        const token = await tokenResponse.json();
        setToken(token.token); // Set the token
    };

    // Check if the user is logged in on component mount
    useEffect(() => {
        const initUser = async () => {
            try {
                await refreshToken();
            } catch (error) {
                console.error('Error fetching user:', error);
            } finally {
                setInitialized(true); // Set initialized to true regardless of success
            }
        };
        initUser();
    }, []); // Empty dependency array ensures this runs only once on mount

    const isLoggedIn = () => jwt_token !== null;

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/users/me', { method: 'GET' }); // No auth header, will fail
        return (await response.json()).csrfToken;
    };

    const login = async (email: string, password: string) => {
        // setUser({ name: username });
        const csrfToken = await fetchCsrfToken();
        const response = await fetch('/api/users/login', {
            method: 'POST',
            body: JSON.stringify({ email, password, csrfToken }),
            headers: {
                'Content-Type': 'application/json',
            }
        });
        const data = await response.json() as LoginResponse;
        if (data.success) {
            setToken(data.token!);
        }
        return data as LoginResponse;
    }

    const loginWithTFA = async (username: string, password: string, tfaCode: string) => {
        const response = await fetch('/api/users/tfa', {
            method: 'POST',
            body: JSON.stringify({ username, password, token: tfaCode }),
            headers: {
                'Content-Type': 'application/json',
            }
        });
        const data = await response.json() as LoginResponse;
        if (data.success) {
            setToken(data.token!);
        }
        return data as LoginResponse;
    }

    const logout = async () => {
        const response = await fetch('/api/users/logout', { method: 'POST' });

        if (!response.ok) {
            console.error('Network response was not ok:', response.statusText);
        } else {
            setToken(null);
        }
    };

    return (
        <AuthContext.Provider value={{ token: jwt_token, initialized, login, logout, loginWithTFA, isLoggedIn, refreshToken }}>
            <UserProvider>
                {children}
            </UserProvider>
        </AuthContext.Provider>
    );
};